import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Table, Button, Card, Badge, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit, FaSync, FaRobot, FaMicrochip, FaDollarSign, FaSave, FaTimes } from 'react-icons/fa';
import aiModelApi from '../../services/aiModelApi';

const ModelConfiguration = ({ setError, setSuccess }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    model_name: '',
    provider: 'OpenAI',
    type: 'streaming',
    maximum_input_tokens: 4096,
    maximum_output_tokens: 1024,
    reasoning: false,
    cost_input_tokens: 0.001,
    cost_output_tokens: 0.002,
    api_endpoint: '',
    tools: 'function_calling'
  });

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    // Handle blur effect when modal is open
    if (showModal) {
      document.body.classList.add('modal-blur-active');
    } else {
      document.body.classList.remove('modal-blur-active');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-blur-active');
    };
  }, [showModal]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await aiModelApi.modelConfig.list();
      if (response && response.success) {
        setModels(response.data?.models || []);
      } else {
        console.error("Failed to fetch models:", response);
        setModels([]);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setError('Error loading models: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (model = null) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        model_name: model.model_name || '',
        provider: model.provider || 'OpenAI',
        type: model.type || 'streaming',
        maximum_input_tokens: model.maximum_input_tokens || 4096,
        maximum_output_tokens: model.maximum_output_tokens || 1024,
        reasoning: model.reasoning || false,
        cost_input_tokens: model.cost_input_tokens || 0.001,
        cost_output_tokens: model.cost_output_tokens || 0.002,
        api_endpoint: model.api_endpoint || '',
        tools: model.tools || 'function_calling'
      });
    } else {
      setEditingModel(null);
      setFormData({
        model_name: '',
        provider: 'OpenAI',
        type: 'streaming',
        maximum_input_tokens: 4096,
        maximum_output_tokens: 1024,
        reasoning: false,
        cost_input_tokens: 0.001,
        cost_output_tokens: 0.002,
        api_endpoint: '',
        tools: 'function_calling'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModel(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Convert numeric fields and handle empty optional fields
      const payload = {
        ...formData,
        maximum_input_tokens: parseInt(formData.maximum_input_tokens) || 0,
        maximum_output_tokens: parseInt(formData.maximum_output_tokens) || 0,
        cost_input_tokens: parseFloat(formData.cost_input_tokens) || 0,
        cost_output_tokens: parseFloat(formData.cost_output_tokens) || 0,
        api_endpoint: formData.api_endpoint || null,
        tools: formData.tools || null
      };

      let response;
      if (editingModel) {
        response = await aiModelApi.modelConfig.edit({
          llm_key: editingModel.llm_key,
          ...payload
        });
      } else {
        response = await aiModelApi.modelConfig.add(payload);
      }

      if (response && response.success) {
        setSuccess(editingModel ? 'Model updated successfully' : 'Model added successfully');
        setShowModal(false);
        fetchModels();
      } else {
        setError(response?.error?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving model:', error);
      setError('Error saving model data: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const getProviderBadge = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'openai': return 'success';
      case 'anthropic': return 'warning';
      case 'bedrock': return 'primary';
      case 'google': return 'danger';
      default: return 'secondary';
    }
  };

  // ENHANCED: Full-Screen Modal Component using React Portal
  // This ensures the modal is rendered at the body level, avoiding z-index issues
  const renderFullScreenModal = () => {
    if (!showModal) return null;

    return createPortal(
      <div className="fullscreen-modal-overlay">
        <div className="fullscreen-modal-container">
          <div className="fullscreen-modal-header">
            <div className="modal-title-section">
              <div className="d-flex align-items-center">
                <FaRobot className="me-3 text-primary" size={32} />
                <div>
                  <h2 className="mb-0">
                    {editingModel ? 'Edit Model Configuration' : 'Add New Model'}
                  </h2>
                  <p className="text-muted mb-0 mt-2">
                    {editingModel 
                      ? `Modifying configuration for ${editingModel.model_name}` 
                      : 'Configure a new AI model for your agents'
                    }
                  </p>
                </div>
              </div>
            </div>
            <button 
              className="close-modal-btn"
              onClick={handleCloseModal}
              disabled={submitting}
              type="button"
            >
              <FaTimes size={24} />
            </button>
          </div>

          <div className="fullscreen-modal-body">
            <form onSubmit={handleSubmit} className="modal-form-container">
              {/* Basic Information Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaRobot />
                  Basic Information
                </h4>
                <Row>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label className="required-field">Model Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="model_name" 
                        value={formData.model_name} 
                        onChange={handleChange} 
                        required 
                        placeholder="e.g. gpt-4-turbo"
                        disabled={!!editingModel}
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        The exact model identifier used by the provider
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label className="required-field">Provider</Form.Label>
                      <Form.Select 
                        name="provider" 
                        value={formData.provider} 
                        onChange={handleChange}
                        size="lg"
                        required
                      >
                        <option value="OpenAI">OpenAI</option>
                        <option value="Anthropic">Anthropic</option>
                        <option value="Bedrock">AWS Bedrock</option>
                        <option value="Google">Google</option>
                        <option value="Azure">Azure OpenAI</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        AI model provider platform
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Model Type</Form.Label>
                      <Form.Select 
                        name="type" 
                        value={formData.type} 
                        onChange={handleChange}
                        size="lg"
                      >
                        <option value="streaming">Streaming</option>
                        <option value="embedding">Embedding</option>
                        <option value="normal">Normal</option>
                        <option value="image_generation">Image Generation</option>
                        <option value="video_generation">Video Generation</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Primary model capability type
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>API Endpoint</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="api_endpoint" 
                        value={formData.api_endpoint} 
                        onChange={handleChange}
                        placeholder="https://api.example.com/v1/..."
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Leave empty to use provider's default endpoint
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Token Limits Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaMicrochip />
                  Token Configuration
                </h4>
                <Row>
                  <Col xl={4} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Maximum Input Tokens</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="maximum_input_tokens" 
                        value={formData.maximum_input_tokens} 
                        onChange={handleChange}
                        min="1"
                        step="1"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Maximum tokens the model can process as input
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={4} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Maximum Output Tokens</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="maximum_output_tokens" 
                        value={formData.maximum_output_tokens} 
                        onChange={handleChange}
                        min="1"
                        step="1"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Maximum tokens the model can generate
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={4} lg={12} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Advanced Reasoning</Form.Label>
                      <div className="reasoning-switch-container">
                        <Form.Check 
                          type="switch"
                          id="reasoning-switch"
                          name="reasoning"
                          label={formData.reasoning ? "Supports Reasoning" : "Basic Model"}
                          checked={formData.reasoning}
                          onChange={handleChange}
                          className="custom-switch"
                        />
                        <Form.Text className="text-muted mt-2">
                          Enable for models with step-by-step reasoning capabilities
                        </Form.Text>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Cost Configuration Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaDollarSign />
                  Cost Configuration
                </h4>
                <Row>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Cost per 1M Input Tokens ($)</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="0.01"
                        name="cost_input_tokens" 
                        value={formData.cost_input_tokens} 
                        onChange={handleChange}
                        min="0"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Cost in USD for processing 1M input tokens
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xl={6} lg={6} md={12}>
                    <Form.Group className="mb-4">
                      <Form.Label>Cost per 1M Output Tokens ($)</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="0.01"
                        name="cost_output_tokens" 
                        value={formData.cost_output_tokens} 
                        onChange={handleChange}
                        min="0"
                        size="lg"
                      />
                      <Form.Text className="text-muted">
                        Cost in USD for generating 1M output tokens
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Capabilities Section */}
              <div className="form-section">
                <h4 className="section-title">
                  <FaMicrochip />
                  Model Capabilities
                </h4>
                <Form.Group className="mb-4">
                  <Form.Label>Supported Tools & Features</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="tools" 
                    value={formData.tools} 
                    onChange={handleChange}
                    placeholder="function_calling, vision, web_search"
                    size="lg"
                  />
                  <Form.Text className="text-muted">
                    Comma-separated list of supported capabilities and tools
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Action Buttons */}
              <div className="modal-form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  <FaTimes className="me-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Saving Configuration...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      {editingModel ? 'Update Model' : 'Create Model'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="model-configuration">
      <Card className="ai-card">
        <div className="ai-card-header">
          <h3 className="ai-card-title">
            <FaRobot />
            Configured Models
          </h3>
          <div className="d-flex gap-3">
            <Button 
              variant="outline-info" 
              size="sm" 
              onClick={fetchModels}
              disabled={loading}
            >
              <FaSync className={loading ? 'fa-spin' : ''} />
              Refresh
            </Button>
            <Button variant="primary" size="sm" onClick={() => handleOpenModal()}>
              <FaPlus />
              Add Model
            </Button>
          </div>
        </div>
        <div className="ai-card-body">
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading models...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="ai-table" hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Model Name</th>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>Context Window</th>
                    <th>Cost (Per 1M)</th>
                    <th>Features</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.length > 0 ? (
                    models.map(model => (
                      <tr key={model.llm_key || model.model_name}>
                        <td>
                          <Badge bg="info" className="model-badge">
                            #{model.llm_key}
                          </Badge>
                        </td>
                        <td>
                          <div className="fw-bold text-white">{model.model_name}</div>
                          {model.tools && (
                            <div className="small text-muted d-flex align-items-center mt-1">
                              <FaMicrochip className="me-1" />
                              {model.tools}
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge bg={getProviderBadge(model.provider)} className="model-badge">
                            {model.provider}
                          </Badge>
                        </td>
                        <td>
                          <span className="text-capitalize">{model.type}</span>
                        </td>
                        <td>
                          <div className="small">
                            <div>In: {model.maximum_input_tokens?.toLocaleString() || 'N/A'}</div>
                            <div>Out: {model.maximum_output_tokens?.toLocaleString() || 'N/A'}</div>
                          </div>
                        </td>
                        <td>
                          <div className="small cost-display">
                            <div><FaDollarSign className="me-1" />{model.cost_input_tokens || 0}/1M In</div>
                            <div><FaDollarSign className="me-1" />{model.cost_output_tokens || 0}/1M Out</div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Badge bg={model.reasoning ? 'success' : 'secondary'} className="model-badge">
                              Reasoning: {model.reasoning ? 'Yes' : 'No'}
                            </Badge>
                            {model.api_endpoint && (
                              <Badge bg="info" className="model-badge">
                                Custom API
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button 
                              variant="outline-info" 
                              size="sm" 
                              onClick={() => handleOpenModal(model)}
                              title="Edit model configuration"
                            >
                              <FaEdit />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center p-5">
                        <div className="text-muted">
                          <FaRobot size={48} className="mb-3 opacity-50" />
                          <p>No models configured. Add your first model to get started.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {/* Enhanced Full-Screen Modal */}
      {renderFullScreenModal()}
    </div>
  );
};

export default ModelConfiguration;
